import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import Button from '@components/common/Button/Button.component'
import { formatAiResponseForHtml } from './useAIResponse'
import { sanitizeStrictHtml } from '@utils/sanitize-html.util'

type AIResponseDisplayProps = {
  aiResponse: string
  showAiActions: boolean
  showEnhanceDropdown: boolean
  onRegenerate: () => void
  onEnhance: (enhanceType: string) => void
  onToggleEnhanceDropdown: () => void
  onCloseEnhanceDropdown: () => void
  renderPreview?: boolean
  isLoading?: boolean
  error?: string | null
}

const enhanceOptions = [
  { text: 'Simplify', value: 'simplify' },
  { text: 'Enhance', value: 'enhance' },
  { text: 'Summarize', value: 'summarize' },
]

const ENHANCE_DROPDOWN_GAP = 4

type EnhanceDropdownPosition = {
  top: number
  left: number
}

const AIResponseDisplay: React.FC<AIResponseDisplayProps> = ({
  aiResponse,
  showAiActions,
  showEnhanceDropdown,
  onRegenerate,
  onEnhance,
  onToggleEnhanceDropdown,
  onCloseEnhanceDropdown,
  renderPreview = true,
  isLoading = false,
  error = null,
}) => {
  const enhanceDropdownRef = useRef<HTMLDivElement | null>(null)
  const enhanceButtonRef = useRef<HTMLButtonElement | null>(null)
  const enhanceMenuRef = useRef<HTMLDivElement | null>(null)
  const [dropdownPosition, setDropdownPosition] =
    useState<EnhanceDropdownPosition | null>(null)
  const loadingDots = Array.from({ length: 3 })

  const renderLoadingState = (): JSX.Element => (
    <div className="ai-response-preview-loading" aria-live="polite">
      <span className="ai-response-preview-loading-label">
        Generating with AI
        <span className="ai-response-preview-loading-dots" aria-hidden="true">
          {loadingDots.map((_, dotIndex) => (
            <span
              key={`dot-${dotIndex}`}
              className="ai-response-preview-loading-dot"
              style={{ animationDelay: `${dotIndex * 0.2}s` }}
            />
          ))}
        </span>
      </span>
      <p className="ai-response-preview-placeholder">
        Synthesizing insights for your note...
      </p>
    </div>
  )

  const updateDropdownPosition = useCallback((): void => {
    const trigger = enhanceButtonRef.current
    const menu = enhanceMenuRef.current
    if (!trigger || !menu) {
      return
    }

    const triggerRect = trigger.getBoundingClientRect()
    const menuRect = menu.getBoundingClientRect()
    let left = triggerRect.right - menuRect.width
    if (left < ENHANCE_DROPDOWN_GAP) {
      left = ENHANCE_DROPDOWN_GAP
    }
    if (left + menuRect.width > window.innerWidth - ENHANCE_DROPDOWN_GAP) {
      left = Math.max(
        ENHANCE_DROPDOWN_GAP,
        window.innerWidth - ENHANCE_DROPDOWN_GAP - menuRect.width,
      )
    }

    const spaceAbove = triggerRect.top
    const spaceBelow = window.innerHeight - triggerRect.bottom
    let top = triggerRect.top - ENHANCE_DROPDOWN_GAP - menuRect.height
    if (
      spaceAbove < menuRect.height + ENHANCE_DROPDOWN_GAP &&
      spaceBelow > spaceAbove
    ) {
      top = triggerRect.bottom + ENHANCE_DROPDOWN_GAP
    }

    setDropdownPosition({ top, left })
  }, [])

  useLayoutEffect(() => {
    if (!showEnhanceDropdown) {
      setDropdownPosition(null)
      return
    }
    updateDropdownPosition()
  }, [showEnhanceDropdown, updateDropdownPosition])

  useEffect(() => {
    if (!showEnhanceDropdown) {
      return
    }

    const handleReposition = (): void => updateDropdownPosition()
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [showEnhanceDropdown, updateDropdownPosition])

  useEffect(() => {
    if (!showEnhanceDropdown) {
      return
    }

    const handleOutsideClick = (event: MouseEvent | TouchEvent): void => {
      const target = event.target as Node | null
      const clickedTrigger =
        !!target &&
        !!enhanceDropdownRef.current &&
        enhanceDropdownRef.current.contains(target)
      const clickedMenu =
        !!target &&
        !!enhanceMenuRef.current &&
        enhanceMenuRef.current.contains(target)
      if (!clickedTrigger && !clickedMenu) {
        onCloseEnhanceDropdown()
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('touchstart', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('touchstart', handleOutsideClick)
    }
  }, [showEnhanceDropdown, onCloseEnhanceDropdown])

  if (!showAiActions) {
    return null
  }

  const formattedResponse = formatAiResponseForHtml(aiResponse)
  const actionsDisabled = isLoading

  return (
    <div className="ai-response-preview">
      {renderPreview ? (
        <div className="ai-response-preview-text">
          {error && !isLoading ? (
            <p className="ai-response-preview-placeholder" role="alert">
              {error}
            </p>
          ) : formattedResponse ? (
            <div
              dangerouslySetInnerHTML={{
                __html: sanitizeStrictHtml(formattedResponse),
              }}
            ></div>
          ) : (
            renderLoadingState()
          )}
        </div>
      ) : (
        <div className="ai-response-preview-text1">
          <span className="ai-response-preview-placeholder1"></span>
          {error && !isLoading ? (
            <span className="ai-response-preview-placeholder" role="alert">
              {error}
            </span>
          ) : null}
        </div>
      )}
      <div className="ai-actions-container">
        <button
          className="ai-action-btn regenerate-btn"
          onClick={onRegenerate}
          title="Regenerate Response"
          disabled={actionsDisabled}
        >
          <i className="mgc mgc_refresh_2_line"></i>
          Regenerate
        </button>
        <div className="enhance-dropdown-container" ref={enhanceDropdownRef}>
          <button
            ref={enhanceButtonRef}
            className="ai-action-btn enhance-btn"
            onClick={onToggleEnhanceDropdown}
            title="Enhance Response"
            disabled={actionsDisabled || !aiResponse.trim()}
          >
            <i className="mgc mgc_magic_3_fill"></i>
            Enhance
            <i
              className={`mgc ${
                showEnhanceDropdown
                  ? 'mgc_up_small_fill'
                  : 'mgc_down_small_fill'
              }`}
            ></i>
          </button>
          {showEnhanceDropdown &&
            createPortal(
              <div
                ref={enhanceMenuRef}
                className="enhance-dropdown"
                style={{
                  top: dropdownPosition?.top ?? 0,
                  left: dropdownPosition?.left ?? 0,
                  visibility: dropdownPosition ? 'visible' : 'hidden',
                }}
              >
                {enhanceOptions.map((option) => (
                  <button
                    key={option.value}
                    className="enhance-option"
                    onClick={() => onEnhance(option.value)}
                  >
                    {option.text}
                  </button>
                ))}
              </div>,
              document.body,
            )}
        </div>
      </div>
    </div>
  )
}

export const AIResponseButtons: React.FC<{
  onInsert: () => void
  onReplace: () => void
  onDismiss?: () => void
  disabled?: boolean
  disabledTitle?: string
}> = ({
  onInsert,
  onReplace,
  onDismiss,
  disabled = false,
  disabledTitle,
}): JSX.Element => {
  return (
    <div className="ai-action-buttons">
      {onDismiss && (
        <Button
          size="sm"
          className="button-secondary"
          onClick={onDismiss}
          title="Dismiss AI response"
        >
          Dismiss
        </Button>
      )}
      <Button
        size="sm"
        className="button-secondary"
        onClick={onReplace}
        disabled={disabled}
        title={disabled ? disabledTitle : 'Replace note with AI response'}
      >
        Replace
      </Button>
      <Button
        size="sm"
        className="button-primary"
        onClick={onInsert}
        disabled={disabled}
        title={disabled ? disabledTitle : 'Insert AI response into note'}
      >
        Insert
      </Button>
    </div>
  )
}

export default AIResponseDisplay
